import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Unlock, ShieldAlert, AlertTriangle, Eye, EyeOff, Fingerprint, Skull } from 'lucide-react';
import ReadOnlyLoyaltyCard from './ReadOnlyLoyaltyCard';

/**
 * 🔐 SECURE VAULT - Coffre-fort spatial sécurisé pour les cartes de fidélité
 * PIN requis + Son d'alerte DANGER à l'ouverture !
 */
export default function SecureVault({ cards = [], customerPin, isDarkMode }) {
  const [isLocked, setIsLocked] = useState(true);
  const [enteredPin, setEnteredPin] = useState(['', '', '', '']);
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const pinRefs = [useRef(), useRef(), useRef(), useRef()];
  const audioContextRef = useRef(null);
  
  // Debug log
  useEffect(() => {
    console.log('🔐 SecureVault - cards reçues:', cards?.length || 0, cards);
  }, [cards]);

  // 🔊 Son d'alerte DANGER - Sirène + Klaxon
  const playDangerSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Sirène montante
      const oscillator1 = audioContext.createOscillator();
      const gainNode1 = audioContext.createGain();
      oscillator1.type = 'sawtooth';
      oscillator1.connect(gainNode1);
      gainNode1.connect(audioContext.destination);
      
      // Klaxon grave
      const oscillator2 = audioContext.createOscillator();
      const gainNode2 = audioContext.createGain();
      oscillator2.type = 'square';
      oscillator2.frequency.setValueAtTime(150, audioContext.currentTime);
      oscillator2.connect(gainNode2);
      gainNode2.connect(audioContext.destination);
      
      // Séquence d'alerte
      const now = audioContext.currentTime;
      
      // Sirène qui monte et descend
      oscillator1.frequency.setValueAtTime(300, now);
      oscillator1.frequency.linearRampToValueAtTime(800, now + 0.3);
      oscillator1.frequency.linearRampToValueAtTime(300, now + 0.6);
      oscillator1.frequency.linearRampToValueAtTime(800, now + 0.9);
      oscillator1.frequency.linearRampToValueAtTime(300, now + 1.2);
      
      gainNode1.gain.setValueAtTime(0.15, now);
      gainNode1.gain.linearRampToValueAtTime(0, now + 1.5);
      
      // Klaxon pulsé
      gainNode2.gain.setValueAtTime(0.1, now);
      gainNode2.gain.setValueAtTime(0, now + 0.15);
      gainNode2.gain.setValueAtTime(0.1, now + 0.3);
      gainNode2.gain.setValueAtTime(0, now + 0.45);
      gainNode2.gain.setValueAtTime(0.1, now + 0.6);
      gainNode2.gain.setValueAtTime(0, now + 0.75);
      gainNode2.gain.setValueAtTime(0.1, now + 0.9);
      gainNode2.gain.linearRampToValueAtTime(0, now + 1.2);
      
      oscillator1.start(now);
      oscillator1.stop(now + 1.5);
      oscillator2.start(now);
      oscillator2.stop(now + 1.2);
      
    } catch (e) {
      console.log('Audio non supporté');
    }
  };

  // 🔊 Son d'erreur
  const playErrorSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('Audio non supporté');
    }
  };

  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newPin = [...enteredPin];
    newPin[index] = value.slice(-1);
    setEnteredPin(newPin);
    setError(false);
    
    // Auto-focus next input
    if (value && index < 3) {
      pinRefs[index + 1].current?.focus();
    }
    
    // Vérifier le PIN si complet
    if (newPin.every(d => d !== '') && newPin.join('') !== '') {
      const fullPin = newPin.join('');
      if (fullPin === customerPin) {
        // PIN CORRECT - Ouvrir le coffre !
        setShowWarning(true);
        setTimeout(() => {
          setIsOpening(true);
          playDangerSound();
          setTimeout(() => {
            setIsLocked(false);
            setIsOpening(false);
            setShowWarning(false);
          }, 1500);
        }, 500);
      } else {
        // PIN INCORRECT
        setError(true);
        playErrorSound();
        setTimeout(() => {
          setEnteredPin(['', '', '', '']);
          pinRefs[0].current?.focus();
        }, 500);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !enteredPin[index] && index > 0) {
      pinRefs[index - 1].current?.focus();
    }
  };

  const lockVault = () => {
    setIsLocked(true);
    setEnteredPin(['', '', '', '']);
  };

  // Filtrer et trier les cartes
  // On montre TOUTES les cartes (actives en premier, puis complétées)
  const activeCards = [...cards]
    .sort((a, b) => {
      // Cartes non complétées en premier
      if (!a.isCompleted && b.isCompleted) return -1;
      if (a.isCompleted && !b.isCompleted) return 1;
      // Puis par progression
      const progressA = a.boxes?.filter(box => box).length || 0;
      const progressB = b.boxes?.filter(box => box).length || 0;
      if (progressA > 0 && progressB === 0) return -1;
      if (progressA === 0 && progressB > 0) return 1;
      return progressB - progressA;
    });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      <div 
        className="relative overflow-hidden rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #7f1d1d 100%)',
          boxShadow: isLocked 
            ? '0 0 40px rgba(239, 68, 68, 0.3), 0 15px 30px rgba(0,0,0,0.5)'
            : '0 0 60px rgba(34, 197, 94, 0.4), 0 15px 30px rgba(0,0,0,0.5)',
          border: `2px solid ${isLocked ? 'rgba(239, 68, 68, 0.5)' : 'rgba(34, 197, 94, 0.5)'}`
        }}
      >
        {/* Étoiles animées */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className={`absolute w-1 h-1 rounded-full ${isLocked ? 'bg-red-400' : 'bg-green-400'}`}
              style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.3 + 0.1 }}
              animate={{ opacity: [0.1, 0.5, 0.1], scale: [1, 1.3, 1] }}
              transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
            />
          ))}
        </div>

        {/* Lignes de scan quand verrouillé */}
        {isLocked && (
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'linear-gradient(transparent 0%, rgba(239, 68, 68, 0.03) 50%, transparent 100%)', backgroundSize: '100% 4px' }}
            animate={{ backgroundPosition: ['0% 0%', '0% 100%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          />
        )}

        {/* Bordure supérieure */}
        <motion.div 
          className="absolute top-0 left-0 w-full h-1"
          style={{ background: `linear-gradient(90deg, transparent, ${isLocked ? '#ef4444' : '#22c55e'}80, transparent)` }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1, repeat: Infinity }}
        />
        
        {/* HUD Corners */}
        <div className={`absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 ${isLocked ? 'border-red-500' : 'border-green-500'}`} />
        <div className={`absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 ${isLocked ? 'border-red-500' : 'border-green-500'}`} />
        <div className={`absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 ${isLocked ? 'border-red-500/50' : 'border-green-500/50'}`} />
        <div className={`absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 ${isLocked ? 'border-red-500/50' : 'border-green-500/50'}`} />

        {/* Warning Animation Overlay */}
        <AnimatePresence>
          {(showWarning || isOpening) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-20 flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.9)' }}
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.1, 1],
                  opacity: [1, 0.8, 1]
                }}
                transition={{ duration: 0.3, repeat: Infinity }}
                className="text-center"
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 0.2, repeat: Infinity }}
                >
                  <AlertTriangle className="w-20 h-20 mx-auto text-yellow-500 mb-4" />
                </motion.div>
                <motion.p 
                  className="text-2xl font-bold text-red-500 font-mono"
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                >
                  ⚠️ ACCÈS AUTORISÉ ⚠️
                </motion.p>
                <p className="text-yellow-400 text-sm mt-2 font-mono">OUVERTURE DU COFFRE EN COURS...</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <div className="relative p-4 flex items-center justify-between border-b border-red-500/20">
          <div className="flex items-center gap-3">
            <motion.div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${isLocked ? 'bg-red-500/20 border-red-500/30' : 'bg-green-500/20 border-green-500/30'} border`}
              animate={{ 
                boxShadow: isLocked 
                  ? ['0 0 10px rgba(239, 68, 68, 0.3)', '0 0 25px rgba(239, 68, 68, 0.5)', '0 0 10px rgba(239, 68, 68, 0.3)']
                  : ['0 0 10px rgba(34, 197, 94, 0.3)', '0 0 25px rgba(34, 197, 94, 0.5)', '0 0 10px rgba(34, 197, 94, 0.3)']
              }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              {isLocked ? (
                <Lock className="w-6 h-6 text-red-400" />
              ) : (
                <Unlock className="w-6 h-6 text-green-400" />
              )}
            </motion.div>
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                {isLocked ? (
                  <>
                    <ShieldAlert className="w-5 h-5 text-red-400" />
                    Coffre Sécurisé
                  </>
                ) : (
                  <>
                    💳 Ta fidélité récompensée
                  </>
                )}
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${
                  isLocked 
                    ? 'bg-red-500/20 text-red-400 border-red-500/30' 
                    : 'bg-green-500/20 text-green-400 border-green-500/30'
                }`}>
                  {isLocked ? 'VERROUILLÉ' : 'DÉVERROUILLÉ'}
                </span>
              </h3>
              <p className={`text-[11px] font-mono tracking-wider ${isLocked ? 'text-red-500/70' : 'text-green-500/70'}`}>
                {isLocked ? 'ZONE RESTREINTE • AUTHENTIFICATION REQUISE' : `${cards.length} CARTE${cards.length > 1 ? 'S' : ''} • ${activeCards.filter(c => !c.isCompleted).length} ACTIVE${activeCards.filter(c => !c.isCompleted).length > 1 ? 'S' : ''}`}
              </p>
            </div>
          </div>
          
          {/* Status + Lock button */}
          <div className="flex items-center gap-3">
            {!isLocked && (
              <motion.button
                onClick={lockVault}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Lock className="w-4 h-4" />
                <span className="text-xs font-mono">VERROUILLER</span>
              </motion.button>
            )}
            <motion.div
              className={`w-3 h-3 rounded-full ${isLocked ? 'bg-red-500' : 'bg-green-500'}`}
              animate={{ opacity: [1, 0.5, 1], scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="relative p-6">
          {isLocked ? (
            /* 🔐 ÉCRAN DE VERROUILLAGE */
            <div className="py-8">
              {/* Icône de coffre animée */}
              <motion.div 
                className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-red-500/20 to-orange-500/20 border-2 border-red-500/40 flex items-center justify-center"
                animate={{ 
                  rotateY: [0, 5, -5, 0],
                  boxShadow: ['0 0 20px rgba(239, 68, 68, 0.2)', '0 0 40px rgba(239, 68, 68, 0.4)', '0 0 20px rgba(239, 68, 68, 0.2)']
                }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Skull className="w-12 h-12 text-red-400" />
                </motion.div>
              </motion.div>

              <motion.p 
                className="text-center text-red-400 font-mono text-sm mb-6"
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                ⚠️ ACCÈS RESTREINT - ENTREZ VOTRE CODE PIN ⚠️
              </motion.p>

              {/* PIN Input */}
              <div className="flex justify-center gap-3 mb-4">
                {enteredPin.map((digit, index) => (
                  <motion.div
                    key={index}
                    animate={error ? { x: [-5, 5, -5, 5, 0] } : {}}
                    transition={{ duration: 0.3 }}
                  >
                    <input
                      ref={pinRefs[index]}
                      type={showPin ? "text" : "password"}
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handlePinChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className={`w-14 h-16 text-center text-2xl font-mono rounded-xl bg-black/50 border-2 outline-none transition-all ${
                        error 
                          ? 'border-red-500 text-red-400 bg-red-500/10' 
                          : digit 
                            ? 'border-yellow-500 text-yellow-400' 
                            : 'border-red-500/30 text-white focus:border-yellow-500'
                      }`}
                      style={{ 
                        boxShadow: digit ? '0 0 15px rgba(234, 179, 8, 0.3)' : 'none',
                        caretColor: 'transparent'
                      }}
                    />
                  </motion.div>
                ))}
              </div>

              {/* Toggle PIN visibility */}
              <div className="flex justify-center mb-4">
                <button
                  onClick={() => setShowPin(!showPin)}
                  className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-400 transition-colors"
                >
                  {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showPin ? 'Masquer' : 'Afficher'}
                </button>
              </div>

              {/* Error message */}
              <AnimatePresence>
                {error && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="text-center text-red-500 text-sm font-mono"
                  >
                    ❌ CODE INCORRECT - ACCÈS REFUSÉ
                  </motion.p>
                )}
              </AnimatePresence>

              {/* Fingerprint hint */}
              <div className="mt-6 text-center">
                <Fingerprint className="w-8 h-8 mx-auto text-gray-600 mb-2" />
                <p className="text-xs text-gray-600">Utilisez votre code PIN client</p>
              </div>
            </div>
          ) : (
            /* 💳 CARTES DE FIDÉLITÉ DÉVERROUILLÉES */
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="space-y-4"
            >
              {/* Légende */}
              <div className="flex flex-wrap gap-4 mb-4 text-sm">
                <div className="flex items-center gap-2">
                  <motion.div 
                    className="w-3 h-3 rounded-full bg-green-500"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                  <span className="text-gray-400">Carte active</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <span className="text-gray-400">Carte complétée 🎉</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-600" />
                  <span className="text-gray-400">Carte vide</span>
                </div>
              </div>

              {/* Cartes */}
              {activeCards.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-gray-400">Aucune carte de fidélité</p>
                  <p className="text-gray-500 text-sm mt-2">Fais des achats pour en obtenir ! 🛒</p>
                </div>
              ) : (
                activeCards.map((card, index) => (
                  <motion.div
                    key={card.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="relative"
                  >
                    {/* Badge carte complétée */}
                    {card.isCompleted && (
                      <div className="absolute -top-2 -right-2 z-10 bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                        🎉 COMPLÈTE !
                      </div>
                    )}
                    <ReadOnlyLoyaltyCard 
                      card={card} 
                      isInactive={!card.isCompleted && (card.boxes?.filter(b => b).length || 0) === 0}
                    />
                  </motion.div>
                ))
              )}
            </motion.div>
          )}
        </div>

        {/* Footer HUD */}
        <div className={`px-4 py-2 border-t ${isLocked ? 'border-red-500/10' : 'border-green-500/10'} flex items-center justify-between`}>
          <span className="text-[10px] font-mono text-gray-600">🔐 SECURE VAULT v2.0</span>
          <div className="flex items-center gap-1">
            <motion.div 
              className={`w-1.5 h-1.5 rounded-full ${isLocked ? 'bg-red-500' : 'bg-green-500'}`}
              animate={{ opacity: [1, 0.5, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
            <div className={`w-1.5 h-1.5 rounded-full ${isLocked ? 'bg-red-400/50' : 'bg-green-400/50'}`} />
            <div className={`w-1.5 h-1.5 rounded-full ${isLocked ? 'bg-red-300/30' : 'bg-green-300/30'}`} />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
